import Entypo from '@expo/vector-icons/Entypo';
import {
  responsiveFontSize,
  responsiveHeight,
  responsiveWidth,
  useResponsiveFontSize,
} from '@/components/Util/reactResponsiveDimensions';
import shuffle from 'lodash/shuffle';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import colors from '@/config/colors';
import { Body } from './body';
import { PressableNeolexon } from '@/components/PressableNeolexon';

export function LesenTextExercise({
  showQuestions,
  back,
  skip,
  settings,
  exerciseState,
  currentExercise,
  exercise,
  checkProgress,
}) {
  const { bottom, left, right, top } = useSafeAreaInsets();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const responsiveIconSize = useResponsiveFontSize(4);
  const [scrollPosition, setScrollPosition] = useState(0);
  const responsiveLineHeight = useResponsiveFontSize(2.7);
  const scrollViewRef = useRef(null);

  const horizontalSafeAreaInsets = left + right;
  const verticalSafeAreaInsets = top + bottom;
  const { questions, type } = exercise;
  const { setting, value } = settings;
  const modality = type === 'Sprechen' ? 'speak' : 'not-speak';
  const showBack = exerciseState === 'questions';

  const [contentHeight, setContentHeight] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const scrollEndOffset = Platform.OS === 'android' ? 80 : 20;

  const hasReachedScrollEnd = (visibleHeight, scrollOffset, contentHeight) => {
    return (
      Math.ceil(visibleHeight + scrollOffset) >= contentHeight - scrollEndOffset
    );
  };

  const responsiveStyles = StyleSheet.create({
    bodyContainer: {
      height: responsiveHeight(62) - verticalSafeAreaInsets,
      width: responsiveWidth(98) - horizontalSafeAreaInsets,
    },
    buttonsContainer: {
      height: responsiveHeight(15) - verticalSafeAreaInsets,
      width: responsiveWidth(90) - horizontalSafeAreaInsets,
    },
    scrollViewContainer: {
      height: responsiveHeight(62) - verticalSafeAreaInsets,
    },
    questionsContainer: {
      width: !showBack ? 0 : '100%',
      height: !showBack ? 0 : '100%',
      opacity: !showBack ? 0 : 1,
      flexDirection: 'row',
      alignItems: 'center',
      zIndex: -1,
    },
    displayNone: {
      width: !showBack ? '100%' : 0,
      height: !showBack ? '100%' : 0,
    },
  });

  const neededQuestions = useMemo(() => {
    let returnValue;
    if (modality !== 'speak') {
      returnValue = questions.filter(function (question) {
        return question.questionType === setting;
      });
    } else {
      if (setting === 'free') {
        returnValue = [
          {
            questionText: 'Erzählen Sie die Geschichte nach.',
            questionAudio: '/audio/erzaehlen_sie_nach.mp3',
          },
        ];
      } else {
        returnValue = questions.filter(function (question) {
          return question.questionText !== 'Erzählen Sie die Geschichte nach.';
        });
      }
    }
    // shuffle order of choices and reduce number of choices according to setting
    if (setting === 'closed') {
      returnValue.forEach(function (question) {
        shuffle(question.choices);
        // calculate how many choices need to be removed to match setting
        let elementsToRemove = question.choices.length - value;
        question.choices = question.choices.filter(function (choice) {
          if (choice.isCorrect === true || elementsToRemove < 1) {
            return true;
          } else {
            elementsToRemove--;
            return false;
          }
        });
        shuffle(question.choices);
      });
    }
    // shuffle order of questions
    shuffle(returnValue);
    return returnValue;
  }, [JSON.stringify(questions), setting, value]);

  const maxNumberOfQuestionIndex = neededQuestions?.length - 1;
  const maxIterations = questionIndex === maxNumberOfQuestionIndex;
  const textContent = useMemo(() => {
    return exercise.textContent.replace(/\[|\]/g, '');
  }, [exercise.textContent]);

  const onWeiterClick = () => {
    if (showBack) {
      if (maxIterations) {
        scrollViewRef.current.scrollTo({
          y: 0,
          animated: false,
        });
        return skip();
      }
      checkProgress(false, true, true);
      setQuestionIndex((prevValue) => prevValue + 1);
    }
    showQuestions();
  };

  const handleScroll = (event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    setScrollPosition(contentOffset.y);

    const isScrolledToEnd = hasReachedScrollEnd(
      layoutMeasurement.height,
      contentOffset.y,
      contentSize.height,
    );

    if (showBack) {
      setIsScrolledToEnd(true);
      return;
    }
    setIsScrolledToEnd(isScrolledToEnd);
  };

  const scrollToTheEnd = () => {
    if (isScrolledToEnd) {
      scrollViewRef.current.scrollToEnd({ animated: true });
    } else {
      scrollViewRef.current.scrollTo({
        y: scrollPosition + 7 * responsiveLineHeight,
        animated: true,
      });
    }
  };

  const scrollUp = () => {
    scrollViewRef.current.scrollTo({
      y: scrollPosition - 7 * responsiveLineHeight,
      animated: true,
    });
  };

  useEffect(() => {
    if (!showBack && isScrolledToEnd) {
      scrollToTheEnd();
    }
  }, [showBack, isScrolledToEnd]);

  useEffect(() => {
    scrollViewRef.current.scrollTo({
      y: 0,
      animated: false,
    });
    setQuestionIndex(0);
    setIsScrolledToEnd(false);
  }, [currentExercise]);

  // Marks content as scrolled if fully visible (no scrolling needed)
  useEffect(() => {
    if (
      contentHeight > 0 &&
      containerHeight > 0 &&
      hasReachedScrollEnd(containerHeight, 0, contentHeight)
    ) {
      setIsScrolledToEnd(true);
    }
  }, [contentHeight, containerHeight]);

  return (
    <SafeAreaView style={styles.container}>
      <View style={[styles.bodyContainer, responsiveStyles.bodyContainer]}>
        <View style={responsiveStyles.questionsContainer}>
          <Body questionIndex={questionIndex} questions={neededQuestions} />
        </View>
        <ScrollView
          onLayout={(e) => setContainerHeight(e.nativeEvent.layout.height)}
          onContentSizeChange={(w, h) => setContentHeight(h)}
          scrollEventThrottle={16}
          onScroll={handleScroll}
          ref={scrollViewRef}
          contentContainerStyle={styles.scrollViewContentContainer}
          style={[
            responsiveStyles.scrollViewContainer,
            responsiveStyles.displayNone,
          ]}
        >
          <View style={styles.titleContainer}>
            <Text style={styles.titleText}>{exercise.title}</Text>
          </View>
          <Text selectable={false} style={styles.textContent}>
            {textContent}
          </Text>
        </ScrollView>
      </View>
      <View
        style={[styles.buttonsContainer, responsiveStyles.buttonsContainer]}
      >
        {showBack ? (
          <PressableNeolexon onPress={back} style={styles.weiterContainer}>
            <Entypo
              name="text-document-inverted"
              size={responsiveIconSize}
              color={colors.darkGrey}
            />
          </PressableNeolexon>
        ) : (
          <PressableNeolexon
            onPress={scrollUp}
            disabled={scrollPosition === 0}
            style={[
              styles.weiterContainer,
              scrollPosition === 0 ? styles.disabledIcon : styles.enabledIcon,
            ]}
          >
            <Entypo
              name="arrow-bold-left"
              size={responsiveIconSize}
              color={colors.black}
            />
          </PressableNeolexon>
        )}
        <PressableNeolexon
          onPress={isScrolledToEnd ? onWeiterClick : scrollToTheEnd}
          style={styles.weiterContainer}
        >
          <Text
            style={[
              styles.responsiveText,
              isScrolledToEnd && styles.weiterStyle,
            ]}
          >
            weiter
          </Text>
          <Entypo
            name="arrow-bold-right"
            size={responsiveIconSize}
            color={isScrolledToEnd ? colors.primary : colors.black}
          />
        </PressableNeolexon>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  disabledIcon: {
    opacity: 0.3,
  },
  enabledIcon: {
    opacity: 1,
  },
  container: {
    flex: 1,
    marginTop: responsiveHeight(1),
    justifyContent: 'center',
    alignItems: 'center',
  },
  bodyContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,
    elevation: 4,
    backgroundColor: colors.white,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: responsiveHeight(1),
  },
  weiterContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weiterStyle: {
    color: colors.primary,
  },
  responsiveText: {
    fontSize: responsiveFontSize(3),
    fontWeight: '700',
    color: colors.black,
  },
  scrollViewContentContainer: {
    paddingVertical: 30,
    paddingHorizontal: 40,
  },
  titleContainer: {
    justifyContent: 'center',
    marginBottom: 10,
  },
  titleText: {
    fontSize: responsiveFontSize(2.4),
    fontWeight: '700',
  },
  textContent: {
    fontSize: responsiveFontSize(2.1),
    fontWeight: '400',
    color: colors.black,
    lineHeight: Platform.OS === 'web' ? 60 : 45,
  },
});
